import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import templateService from "../../../services/template.service";
import clinicService from "../../../services/clinic.service";
import Loading from "../../common/Loading";
import Swal from '../../helpers/swal';
const EditClinicCallTemplate = () => {
    const { clinicId, id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [generatingAudio, setGeneratingAudio] = useState(false);
    const [template, setTemplate] = useState(null);
    const [clinic, setClinic] = useState(null);
    const [formData, setFormData] = useState({
        content: "",
        status: true,
        reminderName: "",
        reminderTypeId: ""
    });
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);

    const fetchTemplateData = async () => {
        try {
            setLoading(true);

            // Get clinic data first
            const clinicResponse = await clinicService.getClinicById(clinicId);
            setClinic(clinicResponse.data);

            // Get template data
            const templateResponse = await templateService.getCallTemplateById(clinicId, id);
            const templateData = templateResponse.data;
            setTemplate(templateData);

            // Set form data
            setFormData({
                content: templateData.content || "",
                status: templateData.status,
                reminderName: templateData.reminderName || "",
                reminderTypeId: templateData.reminderTypeId || ""
            });

        } catch (error) {
            console.error('Error fetching template data:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load template data. Please try again.',
                confirmButtonColor: '#dc3545'
            }).then(() => {
                navigate('/clinic-templates');
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (clinicId && id) {
            fetchTemplateData();
        }

        // Clean up audio URL when component unmounts
        return () => {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
        };
    }, [clinicId, id, navigate]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSaving(true);

            // Prepare the request data including templateId in the body
            const requestData = {
                id: id, // Include templateId in the body
                content: formData.content,
                status: formData.status,
                reminderName: formData.reminderName,
                reminderTypeId: formData.reminderTypeId,
                clinicId: clinicId // Include clinicId in the body if needed
            };

            await templateService.updateCallTemplates(clinicId, id, requestData);

            // Fetch the updated template data from the server to ensure UI reflects the latest state
            await fetchTemplateData();

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Call template updated successfully.',
                confirmButtonColor: '#198754',
                timer: 2000
            });

        } catch (error) {
            console.error('Error updating template:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to update template. Please try again.',
                confirmButtonColor: '#dc3545'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleGenerateAudio = async () => {
        try {
            setGeneratingAudio(true);

            await templateService.generateAudio(clinicId, id);

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Audio generated successfully.',
                confirmButtonColor: '#198754',
                timer: 2000
            });

            // Reload the template data to get the updated audioPath
            await fetchTemplateData();

        } catch (error) {
            console.error('Error generating audio:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to generate audio. Please try again.',
                confirmButtonColor: '#dc3545'
            });
        } finally {
            setGeneratingAudio(false);
        }
    };

    const handlePlayAudio = async () => {
        try {
            // If we already have an audio URL, just play it
            if (audioUrl && audioRef.current) {
                audioRef.current.play();
                setIsPlaying(true);
                return;
            }

            // Call the API to get audio data
            const response = await templateService.playAudio(clinicId, id);

            // Create a blob from the arraybuffer response
            const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });

            // Create a URL for the blob
            const url = URL.createObjectURL(audioBlob);
            setAudioUrl(url);

            // Set the audio source and play it
            if (audioRef.current) {
                audioRef.current.src = url;
                audioRef.current.play().then(() => {
                    setIsPlaying(true);
                }).catch(error => {
                    console.error('Error playing audio:', error);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Failed to play audio. Please try again.',
                        confirmButtonColor: '#dc3545'
                    });
                });

                // Handle when audio finishes playing
                audioRef.current.onended = () => {
                    setIsPlaying(false);
                };

                // Handle audio errors
                audioRef.current.onerror = () => {
                    setIsPlaying(false);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Failed to play audio. Please try again.',
                        confirmButtonColor: '#dc3545'
                    });
                };
            }
        } catch (error) {
            console.error('Error playing audio:', error);
            setIsPlaying(false);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to play audio. Please try again.',
                confirmButtonColor: '#dc3545'
            });
        }
    };

    const handlePauseAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
        }
    };

    const handleCancel = () => {
        navigate('/clinic-templates');
    };

    if (loading) {
        return (
            <div className="container container-xxl">
                <div className="pagetitle">
                    <h1><i className="bi bi-pencil me-2"></i> Edit Call Template</h1>
                </div>
                <Loading />
            </div>
        );
    }

    return (
        <div className="container container-xxl">
            {/* Hidden audio element for playback */}
            <audio ref={audioRef} style={{ display: 'none' }} />

            <div className="pagetitle d-flex justify-content-between align-items-center">
                <h1><i className="bi bi-pencil me-2"></i> Edit Call Template</h1>
                <a className="btn btn-outline-secondary btn-sm" href="/clinic-templates">
                    <i className="bi bi-chevron-left"></i> Back
                </a>
            </div>

            <div className="row">
                <div className="col-lg-8">
                    <div className="card">
                        <div className="card-body">
                            <h5 className="card-title">
                                <i className="bi bi-telephone me-2"></i>
                                Template Details
                            </h5>

                            {clinic && (
                                <div className="alert alert-info">
                                    <i className="bi bi-building me-2"></i>
                                    <strong>Clinic:</strong> {clinic.name} {clinic.city && `- ${clinic.city}`}
                                    <br />
                                    <small className="text-muted">Clinic ID: {clinic.id}</small>
                                </div>
                            )}

                            <form onSubmit={handleSubmit}>
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Reminder Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="reminderName"
                                            value={formData.reminderName}
                                            disabled
                                        />
                                        <div className="form-text">
                                            Descriptive name for this template (e.g., CL_PICKUP, APPOINTMENT_REMINDER)
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold">Status</label>
                                        <div className="form-check form-group form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="status"
                                                checked={formData.status}
                                                onChange={handleInputChange}
                                                style={{ width: '3em', height: '1.5em' }}
                                            />
                                            <label className="form-check-label ms-3 mt-1">
                                                {formData.status ? 'Active' : 'Inactive'}
                                            </label>
                                        </div>
                                        <div className="form-text">
                                            Active templates will be used for automated calls
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Template Content</label>
                                    <textarea
                                        className="form-control"
                                        name="content"
                                        value={formData.content}
                                        onChange={handleInputChange}
                                        required
                                        rows="6"
                                        placeholder="Enter your call template content here..."
                                        style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
                                    />
                                    <div className="form-text">
                                        This text will be converted to speech for automated calls.
                                    </div>
                                    <div className="mt-2">
                                        <span className="badge bg-secondary">
                                            {formData.content.length} characters
                                        </span>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Audio</label>
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            {template.audioPath ? (
                                                <div>
                                                    <div className="d-flex align-items-center mb-2">
                                                        <i className="bi bi-volume-up text-muted me-2"></i>
                                                        <small className="text-muted">Audio Preview:</small>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-2 mb-3">
                                                        {!isPlaying ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary btn-sm"
                                                                onClick={handlePlayAudio}
                                                                disabled={generatingAudio}
                                                            >
                                                                <i className="bi bi-play-circle me-2"></i>
                                                                Play Audio
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="btn btn-warning btn-sm"
                                                                onClick={handlePauseAudio}
                                                            >
                                                                <i className="bi bi-pause-circle me-2"></i>
                                                                Pause Audio
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={handleGenerateAudio}
                                                            disabled={generatingAudio || isPlaying}
                                                        >
                                                            {generatingAudio ? (
                                                                <>
                                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                                    Regenerating...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className="bi bi-arrow-repeat me-2"></i>
                                                                    Regenerate
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                    {isPlaying && (
                                                        <div className="progress mb-2" style={{ height: '5px' }}>
                                                            <div
                                                                className="progress-bar progress-bar-striped progress-bar-animated"
                                                                role="progressbar"
                                                                style={{ width: '100%' }}
                                                            ></div>
                                                        </div>
                                                    )}
                                                    <div className="mt-2">
                                                        <small className="text-muted">
                                                            Audio file: {template.audioPath.split('/').pop()}
                                                        </small>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-center py-3">
                                                    <i className="bi bi-file-earmark-music text-muted display-6"></i>
                                                    <p className="mt-2 mb-3">No audio file generated yet</p>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-sm"
                                                        onClick={handleGenerateAudio}
                                                        disabled={generatingAudio}
                                                    >
                                                        {generatingAudio ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                                Generating...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="bi bi-play-circle me-2"></i>
                                                                Generate Audio
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Template Preview</label>
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center mb-2">
                                                <i className="bi bi-telephone text-muted me-2"></i>
                                                <small className="text-muted">Call Preview:</small>
                                            </div>
                                            <div className="bg-white p-3 rounded border">
                                                {formData.content || (
                                                    <span className="text-muted">Preview will appear here...</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="d-flex justify-content-between align-items-center">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={handleCancel}
                                        disabled={saving}
                                    >
                                        <i className="bi bi-x-circle me-2"></i>
                                        Cancel
                                    </button>
                                    <div className="d-flex gap-2">
                                        <button
                                            type="submit"
                                            className="btn btn-sm btn-success"
                                            disabled={saving}
                                        >
                                            {saving ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-floppy me-2"></i>
                                                    Save
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                <div className="col-lg-4">
                    <div className="card">
                        <div className="card-body">
                            <h6 className="card-title">
                                <i className="bi bi-info-circle me-2"></i>
                                Template Information
                            </h6>

                            <div className="mb-3">
                                <small className="text-muted">Template ID</small>
                                <div className="fw-semibold">{template.id}</div>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted">Created</small>
                                <div className="fw-semibold">{template.createdAt}</div>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted">Last Modified</small>
                                <div className="fw-semibold">{template.modifiedAt}</div>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted">Reminder Type</small>
                                <div className="fw-semibold">{template.reminderTypeId}</div>
                            </div>

                            <div className="alert alert-warning">
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                <strong>Note:</strong> Changes to templates may affect scheduled calls.
                            </div>
                        </div>
                    </div>

                    <div className="card mt-3">
                        <div className="card-body">
                            <h6 className="card-title">
                                <i className="bi bi-lightbulb me-2"></i>
                                Call Template Best Practices
                            </h6>
                            <ul className="list-unstyled small">
                                <li className="mb-2">
                                    <i className="bi bi-check text-success me-2"></i>
                                    Keep messages clear and concise
                                </li>
                                <li className="mb-2">
                                    <i className="bi bi-check text-success me-2"></i>
                                    Speak slowly for better text-to-speech conversion
                                </li>
                                <li className="mb-2">
                                    <i className="bi bi-check text-success me-2"></i>
                                    Include important information at the beginning
                                </li>
                                <li className="mb-2">
                                    <i className="bi bi-check text-success me-2"></i>
                                    Repeat critical details like phone numbers
                                </li>
                                <li className="mb-2">
                                    <i className="bi bi-check text-success me-2"></i>
                                    Include opt-out instructions if needed
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditClinicCallTemplate;